import * as React from 'react'
import styled from 'styled-components'
import { NextSeo } from 'next-seo'

import * as entities from '@owl-nest/entities'

import * as entity from '@boutique/entities'
import * as boutiqueUrl from '@boutique/url'

import { DetailLayout, Rationale, TagList } from '@bob/components'
import { SITE_NAME, SITE_URL } from '@bob/config'
import * as ds from '@bob/design-system'
import * as url from '@bob/url'

import { Head } from './Head'
import { Description } from './Description'
import { AssociatedContents } from './AssociatedContents'
import { Products } from './Products'
import { FutureCommitments } from './FutureCommitments'

export type BrandDetailProps = {
  brand: entity.brand.Brand
  products: entities.product.SearchHitProduct[]
}

export function BrandDetail({ brand, products }: BrandDetailProps): React.ReactElement {
  const pageUrl = url.brand({
    slug: brand.slug,
    name: brand.name,
  })

  const hasAnyNetwork =
    brand.socialNetwork.facebook ||
    brand.socialNetwork.instagram ||
    brand.socialNetwork.twitter ||
    brand.socialNetwork.linkedin

  return (
    <>
      <NextSeo
        title={`${brand.name} sur ${SITE_NAME}`}
        description={brand.descriptionShort}
        canonical={SITE_URL + pageUrl.path}
        openGraph={{
          title: `${brand.name} sur ${SITE_NAME}`,
          description: brand.descriptionShort,
          url: SITE_URL + pageUrl.path,
          // FIXME: when coverImage is set in contentful for all brands, remove null guard
          images: brand.seo
            ? [
                {
                  alt: brand.seo.alt ?? undefined,
                  height: brand.seo.height ?? undefined,
                  url: brand.seo.src,
                  width: brand.seo.width ?? undefined,
                },
              ]
            : undefined,
        }}
        robotsProps={{
          maxImagePreview: 'large',
        }}
      />
      <DetailLayout.Container>
        <ds.BreadCrumbs crumbs={pageUrl.crumbs} />
        <GalleryColumn>
          <ds.Gallery
            initialFocused={0}
            list={brand.images.map((image) => ({
              ...image,
              alt: image.alt ?? undefined,
              width: image.width ?? undefined,
              height: image.height ?? undefined,
            }))}
          />
        </GalleryColumn>
        <DetailLayout.Main>
          <Head brand={brand} />
          <TopCTA>
            {products.length > 0 && (
              <ds.ButtonAsLink
                small
                color="grey"
                outlined
                href={
                  boutiqueUrl.products({
                    query: { facetFilters: { brands: [brand.slug] } },
                  }).path
                }
              >
                Voir les produits
              </ds.ButtonAsLink>
            )}
          </TopCTA>
          <Description brand={brand} />
          <Rationale
            environmentalPillars={brand.pillars.environmental}
            rationale={brand.rationale}
            socialPillars={brand.pillars.social}
            title="Cette marque respecte les critères Bien ou Bien"
          />
          {brand.futureCommitments && (
            <FutureCommitments brand={brand} dangerouslySetInnerHTML={{ __html: brand.futureCommitments }} />
          )}

          <CTAWrapper>
            {brand.brandSiteUrl && (
              <ds.ButtonAsLink small href={brand.brandSiteUrl}>
                Voir le site de la marque
              </ds.ButtonAsLink>
            )}
            {products.length > 0 && (
              <ds.ButtonAsLink
                small
                color="grey"
                outlined
                href={
                  boutiqueUrl.products({
                    query: { facetFilters: { brands: [brand.slug] } },
                  }).path
                }
              >
                Acheter sur Ulule Boutique
              </ds.ButtonAsLink>
            )}
          </CTAWrapper>

          {hasAnyNetwork && (
            <Sharing>
              <ds.styles.copy.Text2>Retrouvez {brand.name} sur les réseaux</ds.styles.copy.Text2>
              <Networks>
                {brand.socialNetwork.facebook && (
                  <a href={brand.socialNetwork.facebook} rel="noopener noreferrer" target="_blank">
                    <ds.icons.Facebook size={24} />
                  </a>
                )}
                {brand.socialNetwork.instagram && (
                  <a href={brand.socialNetwork.instagram} rel="noopener noreferrer" target="_blank">
                    <ds.icons.Instagram size={24} />
                  </a>
                )}
                {brand.socialNetwork.twitter && (
                  <a href={brand.socialNetwork.twitter} rel="noopener noreferrer" target="_blank">
                    <ds.icons.Twitter size={24} />
                  </a>
                )}

                {brand.socialNetwork.linkedin && (
                  <a href={brand.socialNetwork.linkedin} rel="noopener noreferrer" target="_blank">
                    <ds.icons.LinkedIn size={24} />
                  </a>
                )}
              </Networks>
            </Sharing>
          )}
        </DetailLayout.Main>
      </DetailLayout.Container>

      {products.length > 0 && <Products brand={brand} products={products} />}

      <AssociatedContents brand={brand} />

      <SmallContainer>
        {brand.tags.length > 0 && (
          <>
            <ds.styles.heading.TitleCardArticle as="h3">Tags associés</ds.styles.heading.TitleCardArticle>

            <TagList context="brand" tagList={brand.tags} />
          </>
        )}
      </SmallContainer>
    </>
  )
}

const SmallContainer = styled(ds.Container)`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${ds.styles.heading.TitleCardArticle} {
    margin: 20px 0;
  }
`
const TopCTA = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;
`

const CTAWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${ds.ButtonAsLink} {
    margin-bottom: 15px;
  }

  @media screen and (${ds.breakpoint.LAPTOP}) {
    flex-direction: row;
    justify-content: space-evenly;
  }
`

const StickerWrapper = styled.div`
  width: 25%;
  height: 0;
  padding-bottom: 25%;
  position: relative;

  ${ds.stickers.Sticker} {
    min-width: 100px;
    min-height: 100px;
    max-width: 166px;
    max-height: 166px;
    position: absolute;
    width: 100%;
    height: 100%;
  }
`

const GalleryColumn = styled(DetailLayout.GalleryColumn)`
  ${StickerWrapper} {
    position: absolute;
    top: 160px;
    right: 20px;

    @media screen and (${ds.breakpoint.MOBILE_M}) {
      top: 150px;
      right: 0;
    }
    @media screen and (${ds.breakpoint.MOBILE_L}) {
      top: 150px;
      right: -20px;
    }
    @media screen and (${ds.breakpoint.LAPTOP}) {
      right: -60px;
    }
    @media screen and (${ds.breakpoint.LAPTOP_L}) {
      right: -100px;
    }
  }
`

const Sharing = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 40px 0 80px;

  ${ds.styles.copy.Text2} {
    margin-bottom: 20px;
  }
`

const Networks = styled.div`
  display: flex;

  ${ds.icons.Icon} {
    margin: 0 15px;
  }
`
